import { useEffect, useId, useRef } from 'react'
import type { BillingStatus } from '../../api/queries/billing'
import { Button } from '../../ui/Button'
import { PurchaseActions } from './PurchaseActions'
import { formatDate } from './pricing'
import styles from './Billing.module.css'

/**
 * The wall. Shown in front of the upload form when the account cannot start an
 * analysis, and says what the user can do about it — every route the backend
 * offers, with equal weight (Mike, 2026-08-27: "a user may want to buy credits,
 * not a plan, or may be on Solo already").
 *
 * What it says depends on who is behind it: a Free user is offered credits or
 * the plan; a Solo user has no plan to upgrade to and is told when the quota
 * comes back; someone with a failed payment is sent to the card, because
 * credit does not clear a debt.
 */
export function OutOfAnalysesDialog({ status, onClose }: { status: BillingStatus; onClose: () => void }) {
  const titleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const { subscription, actions } = status
  const solo = subscription.plan_code === 'solo'
  const renews = subscription.current_period_end && !subscription.cancel_at_period_end ? formatDate(subscription.current_period_end) : null

  useEffect(() => {
    closeRef.current?.focus()
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  let title = 'No analyses left'
  let body: string
  if (actions.read_only_reason === 'account_read_only') {
    title = 'Your account is read-only'
    body = 'Get in touch and we will sort it out.'
  } else if (actions.read_only_reason) {
    title = 'Payment outstanding'
    body = 'Your last payment failed and the grace period has ended. Update your card under Manage billing to start analysing again.'
  } else if (solo) {
    body = renews
      ? `You have used this month's ${status.usage.included_monthly_analyses} analyses. Buy credits to keep going, they never expire. Or wait for your plan to renew on ${renews}.`
      : `You have used this month's ${status.usage.included_monthly_analyses} analyses. Buy credits to keep going, they never expire.`
  } else if (actions.billing_available) {
    body = 'You have used all the analyses on your plan. Buy credits as you need them, or move to Solo for 50 analyses a month.'
  } else {
    body = 'You have used all the analyses on your plan. Get in touch and we will top you up.'
  }

  return (
    <>
      <div className={styles.overlay} aria-hidden="true" onClick={onClose} />
      <div className={styles.overlay} style={{ background: 'transparent' }}>
        <div className={`${styles.dialog} ${styles.dialogWide}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <h2 id={titleId} className={styles.dialogTitle}>{title}</h2>
          <p className={styles.dialogBody}>{body}</p>
          {/* The recommended step green on the left, Cancel on the right. */}
          <PurchaseActions status={status} primary="first" after={<Button ref={closeRef} onClick={onClose}>Cancel</Button>} />
        </div>
      </div>
    </>
  )
}
